import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useRedirect } from "src/hooks/useRedirect";
import { useBoolean } from "src/hooks/useBoolean";
import { Modal } from "src/components/Modal";
import { useEvent } from "src/hooks/useEvent";
import { useApi } from "src/contexts/api.context";
import { Cluster } from "src/components/Cluster";
import { Form } from "src/components/Form/Form";
import type { Project } from "src/hooks/useProjectQuery";
import { useProjectsQuery } from "src/hooks/useProjectQuery";
import createProject from "src/assets/create-project.svg";
import { observer } from "mobx-react-lite";
import { HomeNavigator } from "src/components/HomeNavigator";
import { Tooltip } from "src/components/Tooltip";
import { IconNote, IconDotsVertical } from "@tabler/icons-react";
import classNames from "classnames";
import { OptionsMenu, Intent } from "src/components/OptionsMenu";
import { DeleteProjectModal } from "src/components/DeleteProjectModal";
import { UpdateProjectModal } from "src/components/UpdateProjectModal";
import { EmptyState } from "src/components/EmptyState";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { LanguageIcon } from "@heroicons/react/24/outline";
import { useTranslation, type Locale } from "src/translations";
import { useFeatureFlag, useFlagIdentity } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";

const CreateProjectModal = ({ onClose }: { onClose: () => void }) => {
  const [projectName, setProjectName] = useState("");
  const redirect = useRedirect();
  const api = useApi();
  const isLoading = useBoolean();
  const { t } = useTranslation();
  const { capture } = useFlagIdentity();
  const postProject = useEvent(async () => {
    isLoading.on();
    const project = await api.PostProject({ label: projectName });
    if (!project) return;
    capture("project_created", { projectId: project.id });
    await api.PostScenario(project.id);
    onClose();
    redirect.project({ projectId: project.id });
  });

  return (
    <Modal.Card onClose={onClose}>
      <Modal.Header onClose={onClose} label={t("home.newProject")} />
      <Form.Text
        type="text"
        label={t("home.projectName")}
        value={projectName}
        onChange={setProjectName}
        autoFocus
      />
      <Modal.Actions>
        <Modal.ActionCancelButton onClick={onClose} label={t("common.cancel")} />
        <Modal.ActionPrimaryButton
          onClick={postProject}
          disabled={!projectName}
          label={t("home.createProject")}
        />
      </Modal.Actions>
    </Modal.Card>
  );
};

export function HomeScreen() {
  const createModal = useBoolean();
  const projects = useProjectsQuery();
  const [searchProjectValue, setSearchProjectValue] = useState("");
  const { t } = useTranslation();

  const projectList = useMemo(
    () =>
      projects.data?.filter((item) =>
        item.name.toLowerCase().includes(searchProjectValue.toLowerCase())
      ),
    [projects.data, searchProjectValue]
  );

  const clearInput = useEvent(() => {
    setSearchProjectValue("");
  });

  return (
    <>
      <div className="overflow-hidden w-screen h-screen flex flex-row bg-back text-gray-200 text-sm">
        <HomeNavigator />
        <div className="flex flex-1 bg-white flex-col relative overflow-hidden">
          <div className="bg-white border-b border-neutral-4 grid grid-cols-3 items-center relative basis-12 px-5">
            <div className="text-header-4 text-neutral-8">{t("home.projects")}</div>
            <Form.Search
              value={searchProjectValue}
              onChange={setSearchProjectValue}
              placeholder={t("home.searchPlaceholder")}
              clear={clearInput}
            />
            <div className="flex justify-end">
              <LanguageToggle />
            </div>
          </div>
          <div className="flex-1 w-full h-full overflow-y-auto bg-neutral-2">
            <Cluster visible={projects.isLoading}>
              <ProgressBar />
            </Cluster>
            <Cluster visible={!projects.isLoading}>
              <div className="flex gap-4 p-4 flex-wrap">
                <CreateProject name={t("home.createNew")} onClick={createModal.on} />
                {projectList?.map((project) => <ProjectCard key={project.id} project={project} />)}
                {projectList?.length === 0 && (
                  <EmptyState title={t("home.noResultsTitle")}>
                    <p>
                      {t("home.noResultsMessage", { search: `#${searchProjectValue}` })}
                    </p>
                  </EmptyState>
                )}
              </div>
            </Cluster>
          </div>
        </div>
      </div>
      <Modal.Portal isOpen={createModal.isOn}>
        <CreateProjectModal onClose={createModal.off} />
      </Modal.Portal>
    </>
  );
}
const ProgressBar = () => {
  const [progress, setProgress] = useState<number>(0);
  const intervalSpeed = 15;
  const incrementSpeed = 5;

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => prevProgress + incrementSpeed);
      if (progress >= 100) {
        clearInterval(progressInterval);
      }
    }, intervalSpeed);

    return () => {
      clearInterval(progressInterval);
    };
  }, [progress]);

  return (
    <div className="grid place-content-center w-full h-full">
      <div className="h-1 relative w-[260px] rounded-full overflow-hidden">
        <div className="w-full h-full bg-primary-3 absolute"></div>
        <div className="h-full bg-primary-5 relative" style={{ width: `${progress}%` }}></div>
      </div>
      <p className="text-center text-body-1 text-black mt-2">{useTranslation().t("home.loadingProjects")}</p>
    </div>
  );
};

const CreateProject = ({ name, onClick }: { name: string; onClick: () => void }) => {
  return (
    <div
      className="z-10 w-[256px] basis-[256px] h-[144px]
      bg-gradient-r from-primary-6 to-neutral-8
      border-[1px] border-neutral-4  cursor-pointer rounded
      flex flex-col text-subtitle-2 text-white p-5
      hover:from-primary-6 hover:to-black  hover:shadow-md hover:shadow-elevation-4"
      onClick={onClick}
    >
      <img className="h-[70%]" src={createProject} alt="createProject" />
      <p className="mx-auto mt-3">{name}</p>
    </div>
  );
};

const ProjectCard = observer(({ project }: { project: Project }) => {
  const redirect = useRedirect();
  const deleteLoading = useBoolean();
  const { t, locale } = useTranslation();
  const truncate = (input: string) => (input?.length > 15 ? `${input.substring(0, 15)}...` : input);
  return (
    <div
      key={project.id}
      className="group relative w-[256px] h-[144px] basis-[256px] bg-white rounded border-neutral-4
      justify-center flex-col border cursor-pointer
      hover:shadow-md hover:shadow-elevation-3"
    >
      <div
        className="w-full"
        onClick={() => {
          if (deleteLoading.isOn) return;
          redirect.project({ projectId: project.id });
        }}
      >
        <div className="w-full h-[16px] bg-gradient from-black via-primary-7 to-black rounded-t"></div>
        <div className="w-full flex flex-col px-3 space-y-1">
          {project.name.length > 15 ? (
            <Tooltip.Trigger text={project.name}>
              <p className="text-subtitle-2 text-neutral-8 mt-2 mb-1">{truncate(project.name)} </p>
            </Tooltip.Trigger>
          ) : (
            <p className="text-subtitle-2 text-neutral-8 mt-2 mb-1">{project.name} </p>
          )}

          <p className="m-0 text-overline text-neutral-6 mt-2">{t("home.savedDesigns")} </p>
          <p className="text-subtitle-2 text-neutral-8">{project.designsCount} </p>
          <LastModified updatedAt={project.updatedAt} />
          <div className="text-caption text-neutral-6">
            <span>{t("home.dateCreated")} </span>
            {new Date(project.createdAt).toLocaleDateString(locale === "de" ? "de-DE" : "en-UK")}
          </div>
        </div>
      </div>
      <KebabMenu project={project} />
    </div>
  );
});

const LastModified = ({ updatedAt }: { updatedAt: string }) => {
  const { t, locale } = useTranslation();
  const RelativeTimeFormat = new Intl.RelativeTimeFormat(locale, { style: "long" });
  const currentDate = new Date().getTime();
  const updateDate = new Date(updatedAt).getTime();
  const timeDifference = currentDate - updateDate;
  const daysAgo = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hoursAgo = Math.floor(timeDifference / (1000 * 60 * 60));
  return (
    <div className="text-caption text-neutral-8">
      {daysAgo > 0 ? (
        <span> {t("home.lastModified")} {RelativeTimeFormat.format(-daysAgo, "day")} </span>
      ) : (
        <span> {t("home.lastModified")} {RelativeTimeFormat.format(-hoursAgo, "hour")}</span>
      )}
    </div>
  );
};

function KebabMenu(props: { project: Project }) {
  const settingsModal = useBoolean();
  const deleteModal = useBoolean();
  const optionsVisible = useBoolean();
  const { t } = useTranslation();
  const notes = props.project.notes ?? "";

  useLayoutEffect(() => {
    window.addEventListener("mousedown", optionsVisible.off);
    return () => window.removeEventListener("mousedown", optionsVisible.off);
  }, [optionsVisible.off]);

  const ref = useRef<HTMLButtonElement>(null);

  return (
    <>
      {notes !== "" && (
        <Tooltip.Trigger
          text={notes}
          className={classNames(
            "absolute top-6 right-8 w-6 h-6 z-40",
            "text-neutral-7 rounded cursor-pointer flex flex-1 items-center space-x-2 flex-shrink-0",
            "hover:text-primary-7 hover:bg-primary-2"
          )}
        >
          <IconNote className="w-4 h-4 shrink-0 text-primary-7 mx-auto" />
        </Tooltip.Trigger>
      )}
      <button
        ref={ref}
        className={classNames(
          "absolute top-6 right-2 w-6 h-6 z-40",
          "text-neutral-7 rounded cursor-pointer flex flex-1 items-center space-x-2 flex-shrink-0",
          "hover:text-primary-7 hover:bg-primary-2"
        )}
        onClick={optionsVisible.on}
        title={t("nav.settings")}
      >
        <IconDotsVertical className="w-4 h-4 shrink-0 text-primary-7 mx-auto" />
      </button>
      <OptionsMenu visible={optionsVisible.isOn} onHide={optionsVisible.off} refRoot={ref.current}>
        <OptionsMenu.Card>
          <OptionsMenu.Button
            label={t("common.edit")}
            Icon={PencilSquareIcon}
            onClick={() => {
              optionsVisible.off();
              settingsModal.on();
            }}
          />
          <OptionsMenu.Separator />
          <OptionsMenu.Button
            intent={Intent.DANGER}
            label={t("common.delete")}
            Icon={TrashIcon}
            onClick={() => {
              optionsVisible.off();
              deleteModal.on();
            }}
          />
        </OptionsMenu.Card>
      </OptionsMenu>
      <Cluster visible={deleteModal.isOn}>
        <DeleteProjectModal
          projectName={props.project.name}
          projectId={props.project.id}
          onClose={deleteModal.off}
        />
      </Cluster>
      <Modal.Portal isOpen={settingsModal.isOn}>
        <UpdateProjectModal
          onClose={settingsModal.off}
          projectName={props.project.name}
          projectId={props.project.id}
          projectNotes={notes}
        />
      </Modal.Portal>
    </>
  );
}

const languages: { locale: Locale; label: string }[] = [
  { locale: "en", label: "English" },
  { locale: "de", label: "Deutsch" },
];

function LanguageToggle() {
  const { locale, setLocale, t } = useTranslation();
  // English is always available; every other locale is gated on the
  // `translations` feature flag until it's ready to ship.
  const translationsEnabled = useFeatureFlag(FLAGS.translations);
  const optionsVisible = useBoolean();
  const ref = useRef<HTMLButtonElement>(null);

  // All hooks must run before any conditional return (Rules of Hooks).
  // Previously the early-return sat above useLayoutEffect — fine when the
  // languages array was statically trimmed, but now the flag can flip the
  // enabled count mid-session and React errors on "rendered more hooks
  // than during the previous render".
  useLayoutEffect(() => {
    window.addEventListener("mousedown", optionsVisible.off);
    return () => window.removeEventListener("mousedown", optionsVisible.off);
  }, [optionsVisible.off]);

  const enabledLanguages = languages.filter(
    (l) => l.locale === "en" || translationsEnabled
  );
  if (enabledLanguages.length <= 1) return null;

  const current = languages.find((l) => l.locale === locale);

  return (
    <>
      <button
        ref={ref}
        className="flex items-center gap-1.5 px-2 py-1 rounded text-neutral-7 hover:text-primary-7 hover:bg-primary-2 cursor-pointer"
        onClick={optionsVisible.on}
        title={t("language.title")}
      >
        <LanguageIcon className="w-4 h-4" />
        <span className="text-body-1">{current?.label}</span>
      </button>
      <OptionsMenu visible={optionsVisible.isOn} onHide={optionsVisible.off} refRoot={ref.current}>
        <OptionsMenu.Card>
          {languages.map((lang) => (
            <OptionsMenu.Button
              key={lang.locale}
              label={lang.label}
              Icon={({ className }) => (
                <LanguageIcon className={className} />
              )}
              onClick={() => {
                setLocale(lang.locale);
                optionsVisible.off();
              }}
            />
          ))}
        </OptionsMenu.Card>
      </OptionsMenu>
    </>
  );
}
